import React, { useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import ThemedChartTooltip, { ThemedTooltipBox } from '../../shared/ThemedChartTooltip';
import ThinScroll from '../../shared/ThinScroll';
import { SkeletonChart } from '../../shared/Skeleton';
import { ErrorState } from '../../shared/StateMessage';
import InfoTip from '../../shared/InfoTip';
import { fmtCurrency, fmtNumber, fmtPercent } from '../../shared/format';
import { BRAND_BLUE } from '../../shared/colors';
import { expandableRowProps } from '../../shared/expandableRow';
import {
  DimInsightCard,
  ExportMenu,
  INSIGHT_MIN_CONVERSIONS,
  downloadCsv,
  downloadPdf,
  makeExport,
} from '../shared';

// Export spec — one column list drives CSV + PDF. CSV emits raw values, PDF emits
// formatted strings + abbreviated headers.
const DOW_EXPORT_COLUMNS = [
  { header: 'Day', get: (day) => day.day_of_week },
  { header: 'Impressions', pdfHeader: 'Impr.', get: (day) => day.impressions, pdf: (day) => day.impressions.toLocaleString() },
  { header: 'Clicks', get: (day) => day.clicks, pdf: (day) => day.clicks.toLocaleString() },
  { header: 'Cost', get: (day) => day.cost, pdf: (day) => fmtCurrency(day.cost) },
  { header: 'Conversions', pdfHeader: 'Conv.', get: (day) => day.conversions, pdf: (day) => fmtNumber(day.conversions, true) },
  { header: 'CPA', get: (day) => day.cpa ?? '', pdf: (day) => (day.cpa != null ? fmtCurrency(day.cpa) : '—') },
  { header: 'CTR', get: (day) => (day.ctr * 100).toFixed(2) + '%', pdf: (day) => fmtPercent(day.ctr) },
];
const DOW_EXPORT = makeExport(DOW_EXPORT_COLUMNS);

const DAY_SHORT = { MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed', THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat', SUNDAY: 'Sun' };
const DAY_FULL = { MONDAY: 'Monday', TUESDAY: 'Tuesday', WEDNESDAY: 'Wednesday', THURSDAY: 'Thursday', FRIDAY: 'Friday', SATURDAY: 'Saturday', SUNDAY: 'Sunday' };
const TOOLTIP_FULL = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' };
// The Google Ads API emits Monday-first; the dashboard convention is Sunday-first
// (DateRangePicker calendar, Website Analytics, CDP), so re-sort client-side.
const DAY_ORDER = { SUNDAY: 0, MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3, THURSDAY: 4, FRIDAY: 5, SATURDAY: 6 };
const sortSundayFirst = (items) => [...items].sort((a, b) => (DAY_ORDER[a.day_of_week] ?? 7) - (DAY_ORDER[b.day_of_week] ?? 7));

const DayOfWeek = ({ dayOfWeekData, loading, error, expanded, onToggle, rangeStart, rangeEnd }) => {
  const [openDays, setOpenDays] = useState({});
  const items = dayOfWeekData?.items?.length ? sortSundayFirst(dayOfWeekData.items) : [];
  const exportCsv = () => {
    if (!items.length) return;
    downloadCsv(DOW_EXPORT.headers, items.map(DOW_EXPORT.toCsvRow), `day-of-week-${rangeStart}-to-${rangeEnd}.csv`);
  };
  const exportPdf = () => {
    if (!items.length) return;
    downloadPdf('Day of Week Performance', DOW_EXPORT.pdfHeaders, items.map(DOW_EXPORT.toPdfRow), `day-of-week-${rangeStart}-to-${rangeEnd}.pdf`, `${rangeStart} to ${rangeEnd}`);
  };

  const buildCustomInsight = () => {
    if (!items.length) return null;
    const totalConv = items.reduce((sum, day) => sum + day.conversions, 0);
    const bestCost = items.reduce((best, day) => day.cost > best.cost ? day : best, items[0]);
    const bestCtr = items.reduce((best, day) => day.ctr > best.ctr ? day : best, items[0]);
    const parts = [];
    parts.push(`${DAY_FULL[bestCost.day_of_week]} has the highest spend at ${fmtCurrency(bestCost.cost)}`);
    if (totalConv >= INSIGHT_MIN_CONVERSIONS) {
      const bestConv = items.reduce((best, day) => day.conversions > best.conversions ? day : best, items[0]);
      const lowestCpa = items.filter(day => day.cpa != null && day.conversions > 0).sort((dayA, dayB) => dayA.cpa - dayB.cpa)[0];
      if (bestConv.conversions > 0) parts.push(`${DAY_FULL[bestConv.day_of_week]} drives the most conversions (${fmtNumber(bestConv.conversions, true)})`);
      if (lowestCpa) parts.push(`${DAY_FULL[lowestCpa.day_of_week]} has the best CPA at ${fmtCurrency(lowestCpa.cpa)}`);
    }
    parts.push(`${DAY_FULL[bestCtr.day_of_week]} has the highest CTR at ${fmtPercent(bestCtr.ctr)}`);
    return { parts, warningSuffix: 'CPA/conversion insights omitted' };
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 px-4 sm:px-5 py-3 sm:py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">Day of Week Performance</span>
          <InfoTip text="Clicks and conversions by day of week. Use to set bid adjustments — push harder on the days that actually book." />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">{expanded ? 'Collapse' : 'Expand'}</span>
          <svg className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t dark:border-gray-800">
          {error ? (
            <div className="pt-4"><ErrorState message={error} /></div>
          ) : loading ? (
            <div className="pt-4">
              <SkeletonChart height={240} />
            </div>
          ) : !items.length ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">No day-of-week data available for this period.</p>
          ) : (
            <div className="pt-4">
              <div className="flex justify-end pb-2">
                <ExportMenu onCsv={exportCsv} onPdf={exportPdf} />
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={items.map((day) => ({ ...day, label: DAY_SHORT[day.day_of_week] }))} margin={{ top: 20, right: 5, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#9ca3af', fontWeight: 500 }} axisLine={{ stroke: '#374151' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(value) => `$${value}`} axisLine={false} tickLine={false} width={50} />
                  <ThemedChartTooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const datum = payload[0].payload;
                      return (
                        <ThemedTooltipBox title={TOOLTIP_FULL[label] || label}>
                          <div>Cost: {fmtCurrency(datum.cost)}</div>
                          <div>Clicks: {datum.clicks.toLocaleString()}</div>
                          <div>Impr: {datum.impressions.toLocaleString()}</div>
                          <div>Conv: {fmtNumber(datum.conversions, true)}</div>
                          <div>CPA: {datum.cpa != null ? fmtCurrency(datum.cpa) : '—'}</div>
                          <div>CTR: {fmtPercent(datum.ctr)}</div>
                        </ThemedTooltipBox>
                      );
                    }}
                  />
                  <Bar dataKey="cost" fill={BRAND_BLUE} radius={[6, 6, 0, 0]} barSize={60} />
                </BarChart>
              </ResponsiveContainer>

              <ThinScroll className="responsive-card-table-wrapper mt-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
                <table className="responsive-card-table w-full text-xs min-w-[640px]">
                  <thead>
                    <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                      <th className="text-left py-2 px-3 font-medium">Day</th>
                      <th className="text-right py-2 px-3 font-medium">Impr.</th>
                      <th className="text-right py-2 px-3 font-medium">Clicks</th>
                      <th className="text-right py-2 px-3 font-medium">Cost</th>
                      <th className="text-right py-2 px-3 font-medium">Conv.</th>
                      <th className="text-right py-2 px-3 font-medium">CPA</th>
                      <th className="text-right py-2 px-3 font-medium">CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((day) => (
                      <tr
                        key={day.day_of_week}
                        {...expandableRowProps({
                          expanded: !!openDays[day.day_of_week],
                          onToggle: () => setOpenDays((prev) => ({ ...prev, [day.day_of_week]: !prev[day.day_of_week] })),
                          label: `metrics for ${day.day_of_week}`,
                          className: `border-b dark:border-gray-800 last:border-0 max-[499px]:cursor-pointer ${openDays[day.day_of_week] ? '' : 'rc-collapsed'}`,
                        })}
                      >
                        <td data-label="Day" className={`rc-title-cell py-2.5 px-3 text-gray-900 dark:text-gray-100 font-medium ${openDays[day.day_of_week] ? 'rc-title-open' : ''}`}>
                          <span className="inline-flex items-center gap-2">
                            <svg className={`hidden max-[499px]:block w-3 h-3 text-gray-400 transition-transform ${openDays[day.day_of_week] ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                            {DAY_FULL[day.day_of_week]}
                          </span>
                        </td>
                        <td data-label="Impr." className="text-right px-3 text-gray-700 dark:text-gray-300">{day.impressions.toLocaleString()}</td>
                        <td data-label="Clicks" className="text-right px-3 text-gray-700 dark:text-gray-300">{day.clicks.toLocaleString()}</td>
                        <td data-label="Cost" className="text-right px-3 text-gray-700 dark:text-gray-300">{fmtCurrency(day.cost)}</td>
                        <td data-label="Conv." className="text-right px-3 text-gray-700 dark:text-gray-300">{fmtNumber(day.conversions, true)}</td>
                        <td data-label="CPA" className="text-right px-3 text-gray-700 dark:text-gray-300">{day.cpa != null ? fmtCurrency(day.cpa) : '—'}</td>
                        <td data-label="CTR" className="text-right px-3 text-gray-700 dark:text-gray-300">{fmtPercent(day.ctr)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ThinScroll>

              <DimInsightCard
                insight={dayOfWeekData.insight}
                totalClicks={items.reduce((sum, day) => sum + day.clicks, 0)}
                totalConv={items.reduce((sum, day) => sum + day.conversions, 0)}
                customParts={buildCustomInsight()}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DayOfWeek;
